// src/redux/groups/groupPostSlice.js
import { createSlice } from '@reduxjs/toolkit';
import {
  getGroupPosts,
  createGroupPost,
  updateGroupPost,
  deleteGroupPost,
  likeGroupPost,
  unlikeGroupPost,
  bullGroupPost,
  unbullGroupPost,
  bearGroupPost,
  unbearGroupPost,
  createGroupComment,
  updateGroupComment,
  deleteGroupComment,
  likeGroupComment,
  unlikeGroupComment,
} from './userGroupesPosts';

const initialState = {
  posts: [],
  hasMore: false,
  status: 'idle',
  error: null,
  loading: false,
  createStatus: 'idle',
  createError: null,
  updateStatus: 'idle',
  updateError: null,
  deleteStatus: 'idle',
  deleteError: null,
};

const dedupeById = (arr) => {
  const seen = new Set();
  const out = [];
  for (const p of arr) {
    if (!seen.has(p.id)) {
      seen.add(p.id);
      out.push(p);
    }
  }
  return out;
};

/**
 * Patches a single post in state.posts by id.
 * Only the keys present in `patch` are updated — all other keys are preserved.
 */
const patchPost = (state, postId, patch) => {
  const idx = state.posts.findIndex((p) => p.id === postId);
  if (idx !== -1) {
    state.posts[idx] = { ...state.posts[idx], ...patch };
  }
};

/** Appends `comment` to the given post's embedded comments array. */
const addComment = (state, postId, comment) => {
  const post = state.posts.find((p) => p.id === postId);
  if (post) {
    post.comments = [...(post.comments || []), comment];
  }
};

/** Replaces one comment in a post's embedded comments array by id. */
const replaceComment = (state, postId, comment) => {
  const post = state.posts.find((p) => p.id === postId);
  if (post) {
    post.comments = (post.comments || []).map((c) => (c.id === comment.id ? comment : c));
  }
};

/** Removes one comment from a post's embedded comments array by id (server soft-deletes it). */
const removeComment = (state, postId, commentId) => {
  const post = state.posts.find((p) => p.id === postId);
  if (post) {
    post.comments = (post.comments || []).filter((c) => c.id !== commentId);
  }
};

/** Flips a comment's optimistic like state after a confirmed like/unlike. */
const setCommentLiked = (state, postId, commentId, isLiked) => {
  const post = state.posts.find((p) => p.id === postId);
  if (!post) return;
  post.comments = (post.comments || []).map((c) =>
    c.id === commentId
      ? { ...c, is_liked: isLiked, like_count: Math.max(0, (c.like_count || 0) + (isLiked ? 1 : -1)) }
      : c
  );
};

const groupPostSlice = createSlice({
  name: 'groupPosts',
  initialState,
  reducers: {
    resetGroupPosts: (state) => {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    // ── GET ──────────────────────────────────────────────────────────────────
    builder
      .addCase(getGroupPosts.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
        state.error = null;
      })
      .addCase(getGroupPosts.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loading = false;
        const { posts, has_more, skip } = action.payload;
        if (skip && skip > 0) {
          state.posts = dedupeById([...state.posts, ...posts]);
        } else {
          state.posts = posts;
        }
        state.hasMore = !!has_more;
      })
      .addCase(getGroupPosts.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.payload || action.error?.message || 'Failed to fetch posts';
      });

    // ── CREATE ───────────────────────────────────────────────────────────────
    builder
      .addCase(createGroupPost.pending, (state) => {
        state.createStatus = 'loading';
        state.loading = true;
        state.createError = null;
      })
      .addCase(createGroupPost.fulfilled, (state, action) => {
        state.createStatus = 'succeeded';
        state.loading = false;
        state.posts = dedupeById([action.payload, ...state.posts]);
      })
      .addCase(createGroupPost.rejected, (state, action) => {
        state.createStatus = 'failed';
        state.loading = false;
        state.createError = action.payload || action.error?.message || 'Failed to create post';
      });

    // ── UPDATE ───────────────────────────────────────────────────────────────
    builder
      .addCase(updateGroupPost.pending, (state) => {
        state.updateStatus = 'loading';
        state.loading = true;
        state.updateError = null;
      })
      .addCase(updateGroupPost.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        state.loading = false;
        const updated = action.payload;
        state.posts = state.posts.map((p) => (p.id === updated.id ? updated : p));
      })
      .addCase(updateGroupPost.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.loading = false;
        state.updateError = action.payload || action.error?.message || 'Failed to update post';
      });

    // ── DELETE ───────────────────────────────────────────────────────────────
    builder
      .addCase(deleteGroupPost.pending, (state) => {
        state.deleteStatus = 'loading';
        state.loading = true;
        state.deleteError = null;
      })
      .addCase(deleteGroupPost.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.loading = false;
        const { postId } = action.payload;
        state.posts = state.posts.filter((p) => p.id !== postId);
      })
      .addCase(deleteGroupPost.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.loading = false;
        state.deleteError = action.payload || action.error?.message || 'Failed to delete post';
      });

    // ── LIKE ─────────────────────────────────────────────────────────────────
    // None of the vote/like/comment `.rejected` cases below touch `state.error`
    // -- that field also gates GroupPosts.jsx's top-level
    // `if (error) return <ErrorDisplay .../>`, which is meant for a failed
    // *list fetch* (getGroupPosts, above). Wiring per-post action failures
    // into the same field replaced the entire posts list with a full-page
    // error card over one failed vote/like/comment -- confirmed live via
    // Playwright (forcing a single bear-vote 500 removed every post from the
    // page, not just that button). These actions already surface locally:
    // reactingKeys/isLoading in GroupPosts.jsx and isVoting/localBullVote in
    // Post.jsx cover in-flight + revert UX per button. console.warn stays for
    // dev-console visibility only.
    builder
      .addCase(likeGroupPost.fulfilled, (state, action) => {
        const { postId, likes, is_liked } = action.payload;
        patchPost(state, postId, { likes, is_liked });
      })
      .addCase(likeGroupPost.rejected, (state, action) => {
        console.warn('[groupPostSlice] likeGroupPost rejected:', action.payload);
      });

    // ── UNLIKE ───────────────────────────────────────────────────────────────
    builder
      .addCase(unlikeGroupPost.fulfilled, (state, action) => {
        const { postId, likes, is_liked } = action.payload;
        patchPost(state, postId, { likes, is_liked });
      })
      .addCase(unlikeGroupPost.rejected, (state, action) => {
        console.warn('[groupPostSlice] unlikeGroupPost rejected:', action.payload);
      });

    // ── BULL ─────────────────────────────────────────────────────────────────
    builder
      .addCase(bullGroupPost.fulfilled, (state, action) => {
        // Backend returns { post_id, i_bulled, i_beared, bull_count, bear_count }
        const { postId, i_bulled, i_beared, bull_count, bear_count } = action.payload;
        patchPost(state, postId, { i_bulled, i_beared, bull_count, bear_count });
      })
      .addCase(bullGroupPost.rejected, (state, action) => {
        console.warn('[groupPostSlice] bullGroupPost rejected:', action.payload);
      });

    // ── UNBULL ───────────────────────────────────────────────────────────────
    builder
      .addCase(unbullGroupPost.fulfilled, (state, action) => {
        const { postId, i_bulled, bull_count, bear_count } = action.payload;
        patchPost(state, postId, { i_bulled, bull_count, bear_count });
      })
      .addCase(unbullGroupPost.rejected, (state, action) => {
        console.warn('[groupPostSlice] unbullGroupPost rejected:', action.payload);
      });

    // ── BEAR ─────────────────────────────────────────────────────────────────
    builder
      .addCase(bearGroupPost.fulfilled, (state, action) => {
        const { postId, i_beared, i_bulled, bull_count, bear_count } = action.payload;
        patchPost(state, postId, { i_beared, i_bulled, bull_count, bear_count });
      })
      .addCase(bearGroupPost.rejected, (state, action) => {
        console.warn('[groupPostSlice] bearGroupPost rejected:', action.payload);
      });

    // ── UNBEAR ───────────────────────────────────────────────────────────────
    builder
      .addCase(unbearGroupPost.fulfilled, (state, action) => {
        const { postId, i_beared, bull_count, bear_count } = action.payload;
        patchPost(state, postId, { i_beared, bull_count, bear_count });
      })
      .addCase(unbearGroupPost.rejected, (state, action) => {
        console.warn('[groupPostSlice] unbearGroupPost rejected:', action.payload);
      });

    // ── COMMENTS ─────────────────────────────────────────────────────────────
    builder
      .addCase(createGroupComment.fulfilled, (state, action) => {
        const { postId, comment } = action.payload;
        addComment(state, postId, comment);
      })
      .addCase(createGroupComment.rejected, (state, action) => {
        console.warn('[groupPostSlice] createGroupComment rejected:', action.payload);
      });

    builder
      .addCase(updateGroupComment.fulfilled, (state, action) => {
        const { postId, comment } = action.payload;
        replaceComment(state, postId, comment);
      })
      .addCase(updateGroupComment.rejected, (state, action) => {
        console.warn('[groupPostSlice] updateGroupComment rejected:', action.payload);
      });

    builder
      .addCase(deleteGroupComment.fulfilled, (state, action) => {
        const { postId, commentId } = action.payload;
        removeComment(state, postId, commentId);
      })
      .addCase(deleteGroupComment.rejected, (state, action) => {
        console.warn('[groupPostSlice] deleteGroupComment rejected:', action.payload);
      });

    builder
      .addCase(likeGroupComment.fulfilled, (state, action) => {
        const { postId, commentId } = action.payload;
        setCommentLiked(state, postId, commentId, true);
      })
      .addCase(likeGroupComment.rejected, (state, action) => {
        console.warn('[groupPostSlice] likeGroupComment rejected:', action.payload);
      });

    builder
      .addCase(unlikeGroupComment.fulfilled, (state, action) => {
        const { postId, commentId } = action.payload;
        setCommentLiked(state, postId, commentId, false);
      })
      .addCase(unlikeGroupComment.rejected, (state, action) => {
        console.warn('[groupPostSlice] unlikeGroupComment rejected:', action.payload);
      });
  },
});

export const { resetGroupPosts } = groupPostSlice.actions;
export default groupPostSlice.reducer;